import React, { useEffect, useState } from "react";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { useParams } from "react-router-dom";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Pagination,
} from "@nextui-org/react";
import { StandardSecondaryButton } from "../../components/StandardButtons";
import {
  GetAllMedia,
  getDataWithFilterUrl,
} from "../../controllers/APIController";
import NullData from "../../components/ErrorPage/NullData";
import SingleSearchComponent from "./SingleSearchComponent";
import LoadingPage from "../../components/Loader/LoadingPage";

// Available filter categories for search results
const availableFilterCategories = [
  "Reference Data",
  "Environment",
  "Social",
  "Governance",
  "Evidence",
];

/**
 * SearchPage Component
 * Main search results page displaying filtered search results
 * Supports filtering by category and pagination
 * @returns {JSX.Element} Search results page with filters and pagination
 */
const SearchPage = () => {
  // URL parameters
  const { _searchText: searchText } = useParams();
  
  // Component state
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchedData, setSearchedData] = useState([]);
  const [filteredSearchData, setFilteredSearchData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(new Set([]));

  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;

  // API filter URL for organization-specific data
  const organizationFilterUrl = `filters[$or][0][Organization][id][$eqi]=${orgInfo}&filters[$or][1][Category][$eqi]=Reference Data`;

  /**
   * Fetch and process search data on component mount
   * Combines metrics and media data based on search text
   */
  useEffect(() => {
    let combinedDataList = [];
    
    // Fetch metrics data
    getDataWithFilterUrl("metrics", organizationFilterUrl)
      .then((metricsData) => {
        metricsData?.data?.forEach((metric) => {
          if (
            !metric?.attributes?.Name?.toLowerCase().includes(
              searchText.toLowerCase()
            )
          ) return;
          
          combinedDataList.push({
            id: metric.id,
            type: "Metric",
            Name: metric?.attributes?.Name,
            category: metric?.attributes?.Category,
            description: metric?.attributes?.Description || "---",
            TagList: metric?.attributes?.TagList || [],
          });
        });
        
        // Fetch media/evidence data
        GetAllMedia().then((mediaData) => {
          mediaData?.forEach((mediaItem) => {
            if (
              !mediaItem?.name?.toLowerCase().includes(searchText.toLowerCase())
            ) return;
            
            combinedDataList.push({
              id: mediaItem.id,
              type: "Evidence",
              Name: mediaItem?.name,
              url: mediaItem?.url,
            });
          });
          
          setSearchedData(combinedDataList);
          setFilteredSearchData(combinedDataList);
          setIsLoading(false);
        });
      })
      .catch((error) => {
        console.error("Error fetching metrics data:", error);
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Filter search results based on selected categories
   * Updates filtered data when filters change
   */
  useEffect(() => {
    const selectedFilterValues = Array.from(selectedFilters);
    const filteredList = searchedData?.filter((data) => {
      if (
        selectedFilterValues.includes(data.type) ||
        selectedFilterValues.includes(data.category) ||
        selectedFilterValues.length === 0
      ) {
        return true;
      }
      return false;
    });

    setFilteredSearchData(filteredList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  // Pagination calculations
  const resultCount = filteredSearchData.length;
  const itemsPerPage = 15;
  const totalPages = Math.ceil(resultCount / itemsPerPage);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Search Results</h1>
            <p className="text-sm text-slate-600 mt-1">Search results for "{searchText}"</p>
          </div>
          <div className="text-sm text-slate-600">
            Found <span className="font-semibold text-slate-900">{resultCount}</span> results
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          <LoadingPage isLoading={isLoading}>
            {/* Filters Section */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Filter Results</h2>
                <Dropdown className="w-max">
                  <DropdownTrigger>
                    <StandardSecondaryButton variant="ghost" className="text-slate-600 hover:text-slate-900 px-4">
                      Filters
                    </StandardSecondaryButton>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Multiple selection"
                    variant="flat"
                    closeOnSelect={false}
                    selectionMode="multiple"
                    selectedKeys={selectedFilters}
                    onSelectionChange={setSelectedFilters}
                    className="custom-dropdown-menu"
                  >
                    {availableFilterCategories?.map((categoryName) => (
                      <DropdownItem key={categoryName}>{categoryName}</DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
            
            {/* Search Results Section */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Search Results</h3>
              
              {filteredSearchData.length > 0 ? (
                <>
                  {/* Results List */}
                  <div className="space-y-4">
                    {filteredSearchData.map((item, index) => {
                      const lowerPageLimit = (currentPage - 1) * itemsPerPage;
                      const upperPageLimit = currentPage * itemsPerPage;
                      
                      if (index < lowerPageLimit || index >= upperPageLimit) {
                        return null;
                      }
                      
                      return <SingleSearchComponent key={item.id} data={item} />;
                    })}
                  </div>
                  
                  {/* Pagination Controls */}
                  {filteredSearchData.length > 0 && (
                    <div className="w-full mt-8 flex items-center justify-center pt-6 border-t border-slate-200">
                      <Pagination
                        total={totalPages}
                        color="primary"
                        page={currentPage}
                        onChange={setCurrentPage}
                        showControls
                        size="sm"
                        classNames={{
                          item: "bg-transparent hover:bg-slate-100",
                          cursor: "bg-emerald-600 text-white",
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                /* No Results Display */
                <div className="py-12">
                  <NullData title="No search results found" />
                </div>
              )}
            </div>
          </LoadingPage>
        </div>
      </BreadCrumbsPage>
    </div>
  );
};

export default SearchPage;
